import { externalOperatorService } from './externalOperatorService';
import { PaginatedResponse } from '@/types';

export interface ExternalOperatorDriver {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  workPhoneNumber: string | null;
  status: 'PENDING' | 'ACTIVE' | 'INACTIVE' | 'DELETED';
  currentOperatorId: string | null;
  currentOperator: string | null;
}

export interface ExternalOperatorDriverLookup {
  id: string;
  firstName: string;
  lastName: string;
  status: ExternalOperatorDriver['status'];
  maskedWorkPhoneNumber: string | null;
  linkedToMyOperator: boolean;
}

export interface ExternalOperatorDriversFilters {
  name?: string;
  lastName?: string;
  email?: string;
  workPhoneNumber?: string;
  assignableToOperatorId?: string;
  assignableToRouteOperatorId?: string;
}

interface GetExternalOperatorDriversArgs {
  filters?: ExternalOperatorDriversFilters;
  limit?: number;
  offset?: number;
}

const externalOperatorDriversApi = externalOperatorService.injectEndpoints({
  endpoints: (builder) => ({
    getExternalOperatorDrivers: builder.query<
      PaginatedResponse<ExternalOperatorDriver>,
      GetExternalOperatorDriversArgs | void
    >({
      query: (args) => {
        const { filters, limit, offset } = args ?? {};
        return {
          url: '/external-operator/drivers',
          params: {
            limit: limit ?? 10,
            offset: offset ?? 0,
            ...(filters?.name && { name: filters.name }),
            ...(filters?.lastName && { lastName: filters.lastName }),
            ...(filters?.email && { email: filters.email }),
            ...(filters?.workPhoneNumber && {
              workPhoneNumber: filters.workPhoneNumber,
            }),
            ...(filters?.assignableToRouteOperatorId && {
              assignableToRouteOperatorId: filters.assignableToRouteOperatorId,
            }),
            ...(filters?.assignableToOperatorId && {
              assignableToOperatorId: filters.assignableToOperatorId,
            }),
          },
        };
      },
      transformResponse: (
        response: PaginatedResponse<ExternalOperatorDriver>
      ) => ({
        count: response?.count ?? 0,
        items: Array.isArray(response?.items) ? response.items : [],
      }),
      providesTags: ['ExternalOperatorDrivers'],
    }),
    enableExternalOperatorDriver: builder.mutation<
      { id: string; status: ExternalOperatorDriver['status'] },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/external-operator/drivers/${id}/enable`,
        method: 'POST',
      }),
      invalidatesTags: ['ExternalOperatorDrivers'],
    }),
    findExternalOperatorDriverByDocument: builder.query<
      ExternalOperatorDriverLookup,
      { documentNumber: string }
    >({
      query: ({ documentNumber }) => ({
        url: '/external-operator/drivers/lookup',
        params: { documentNumber },
      }),
      providesTags: ['ExternalOperatorDrivers'],
    }),
    assignExternalOperatorDriverOperator: builder.mutation<
      void,
      { driverId: string; logisticOperatorId: string }
    >({
      query: ({ driverId, logisticOperatorId }) => ({
        url: `/external-operator/drivers/${driverId}/assign-operator`,
        method: 'POST',
        body: { logisticOperatorId },
      }),
    }),
    unlinkExternalOperatorDriver: builder.mutation<
      { id: string; currentOperatorId: string | null },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/external-operator/drivers/${id}/unlink`,
        method: 'POST',
      }),
      invalidatesTags: ['ExternalOperatorDrivers'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetExternalOperatorDriversQuery,
  useEnableExternalOperatorDriverMutation,
  useLazyFindExternalOperatorDriverByDocumentQuery,
  useAssignExternalOperatorDriverOperatorMutation,
  useUnlinkExternalOperatorDriverMutation,
} = externalOperatorDriversApi;
